import { WORKFLOW_HISTORY } from "@sagot/constants";
import { useCallback, useReducer } from "react";
import type { Definition } from "./types";

/** How an edit is filed in the history. */
export interface EditOptions {
  /** What the edit did, for the Undo / Redo tooltips ("connect Book a tour to Path D"). */
  label: string;
  /** Edits with the same key within `WORKFLOW_HISTORY.coalesceMs` of each other count as one (typing in a field). */
  coalesceKey?: string;
}

/** A version of the definition with what it took to get there from the one before it. */
interface Entry {
  definition: Definition;
  label: string;
}

export interface History {
  /** Older versions, oldest first; each carries the label of the edit made on top of it. */
  past: readonly Entry[];
  present: Definition;
  /** Versions undone, the next redo first; each carries the label of the edit that made the one after it. */
  future: readonly Entry[];
  /** The key and time of the last edit, to merge the next one into it. */
  last: { key: string; at: number } | null;
}

/** How the builder reports an edit: the new definition, and optionally what to call it and how to merge it with the one before. */
export type OnEdit = (next: Definition, options?: Partial<EditOptions>) => void;

export const startHistory = (present: Definition): History => ({ past: [], present, future: [], last: null });

/** The definition after an edit. The same object (nothing changed) is not an edit; a new edit clears what could be redone. */
export function commitEdit(history: History, next: Definition, options: EditOptions, now: number): History {
  if (next === history.present) return history;
  const { coalesceKey } = options;
  const merges = coalesceKey !== undefined && history.last?.key === coalesceKey && now - history.last.at <= WORKFLOW_HISTORY.coalesceMs && history.past.length > 0;
  const last = coalesceKey === undefined ? null : { key: coalesceKey, at: now };
  if (merges) return { ...history, present: next, future: [], last };
  const past = [...history.past, { definition: history.present, label: options.label }];
  return { past: past.slice(-WORKFLOW_HISTORY.max), present: next, future: [], last };
}

export function undoEdit(history: History): History {
  const entry = history.past.at(-1);
  if (!entry) return history;
  return { past: history.past.slice(0, -1), present: entry.definition, future: [{ definition: history.present, label: entry.label }, ...history.future], last: null };
}

export function redoEdit(history: History): History {
  const [entry, ...rest] = history.future;
  if (!entry) return history;
  return { past: [...history.past, { definition: history.present, label: entry.label }].slice(-WORKFLOW_HISTORY.max), present: entry.definition, future: rest, last: null };
}

type Action =
  | { type: "commit"; next: Definition; options: EditOptions; now: number }
  | { type: "undo" }
  | { type: "redo" }
  | { type: "reset"; definition: Definition };

function reduce(history: History, action: Action): History {
  switch (action.type) {
    case "commit":
      return commitEdit(history, action.next, action.options, action.now);
    case "undo":
      return undoEdit(history);
    case "redo":
      return redoEdit(history);
    case "reset":
      return startHistory(action.definition);
  }
}

export interface DefinitionHistory {
  definition: Definition;
  commit: (next: Definition, options: EditOptions) => void;
  undo: () => void;
  redo: () => void;
  /** Forgets every version and starts again from `definition` (the workflow was loaded again, rolled back or replaced). */
  reset: (definition: Definition) => void;
  /** What undoing / redoing would take back / bring back, or `null` when there is nothing to undo / redo. */
  undoLabel: string | null;
  redoLabel: string | null;
}

/**
 * The definition being edited together with the versions before and after it (WF-P5-14): at most
 * `WORKFLOW_HISTORY.max` of them, kept across saves and forgotten when the page is loaded again.
 */
export function useDefinitionHistory(initial: () => Definition): DefinitionHistory {
  const [history, dispatch] = useReducer(reduce, undefined, () => startHistory(initial()));
  const commit = useCallback((next: Definition, options: EditOptions) => { dispatch({ type: "commit", next, options, now: Date.now() }); }, []);
  const undo = useCallback(() => { dispatch({ type: "undo" }); }, []);
  const redo = useCallback(() => { dispatch({ type: "redo" }); }, []);
  const reset = useCallback((definition: Definition) => { dispatch({ type: "reset", definition }); }, []);
  return {
    definition: history.present,
    commit,
    undo,
    redo,
    reset,
    undoLabel: history.past.at(-1)?.label ?? null,
    redoLabel: history.future[0]?.label ?? null,
  };
}
